import { FC, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import { CurrencyField } from '@/components/common/CurrencyField'
import { elegibilidadeReservaService } from '@/services/elegibilidadeReservaService'
import { mensagemErroElegibilidade } from '@/utils/errosElegibilidade'
import type { Papel } from '@/components/layout/menu'
import {
  ROTULO_FONTE_VALOR_IMOVEL,
  ROTULO_RESULTADO_ELEGIBILIDADE,
  ROTULO_ROTA_OPERACAO,
  RotaOperacao,
} from '@/types/elegibilidade'
import {
  ElegibilidadeReserva,
  ROTULO_DECISAO_ELEGIBILIDADE,
  ROTULO_MOMENTO_AVALIACAO,
  ROTULO_ORIGEM_FATURAMENTO,
  ROTULO_ORIGEM_ROTA,
} from '@/types/elegibilidadeReserva'
import { AlertaElegibilidade } from './AlertaElegibilidade'
import { CamposImovel, ValoresImovel } from '@/components/elegibilidade/CamposImovel'
import { ChipResultadoElegibilidade, ChipRotaOperacao } from '@/components/elegibilidade/ChipElegibilidade'
import { DialogoMotivoElegibilidade } from '@/components/elegibilidade/DialogoMotivoElegibilidade'
import { fracaoComoPercentual, formatarDataIso } from '@/components/elegibilidade/formatoElegibilidade'
import { formatCurrency, formatDateTime } from '@/utils/formatters'

export interface SecaoElegibilidadeReservaProps {
  oportunidadeId: string
  /** Persona pelo `papelPrincipal` (CONSULTOR com OPERADOR residual continua consultor). */
  papel: Papel | null
  /**
   * `cadastro`: etapa 3 / retomada da Nova Reserva — resultado + dados declarados editáveis.
   * `detalhe`: detalhe da reserva — também histórico, rota, decisões e as ações do back-office/ADMIN.
   * `resumo`: drawer da Curadoria — só leitura (resultado, explicações, dados declarados, rota).
   */
  modo?: 'cadastro' | 'detalhe' | 'resumo'
  onAlterada?: (e: ElegibilidadeReserva) => void
}

const ehBackoffice = (p: Papel | null) => p === 'ADMIN' || p === 'GERENTE' || p === 'OPERADOR'

const Linha: FC<{ rotulo: string; valor: string; detalhe?: string | null }> = ({ rotulo, valor, detalhe }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" component="div">
      {rotulo}
    </Typography>
    <Typography variant="body2" component="div">
      {valor}
    </Typography>
    {detalhe && (
      <Typography variant="caption" color="text.secondary" component="div">
        {detalhe}
      </Typography>
    )}
  </Box>
)

/**
 * P2 (AN-PP2 §8) — seção "Elegibilidade" da reserva: o snapshot que o SERVIDOR gravou
 * (`GET /v1/oportunidades/{id}/elegibilidade`), os dados declarados (Q69: faturamento com
 * origem, data e versão; Home Equity: imóvel, fonte, valor solicitado) e a rota.
 *
 * Ações: dados declarados editáveis (`PUT …/dados` — o servidor decide quem e até quando:
 * 409 `ELEGIBILIDADE_CONGELADA`); back-office "Reavaliar"; ADMIN "Alterar rota" e "Sobrepor
 * impedimento" com motivo obrigatório (Q67). Nada é calculado aqui.
 */
export const SecaoElegibilidadeReserva: FC<SecaoElegibilidadeReservaProps> = ({
  oportunidadeId,
  papel,
  modo = 'detalhe',
  onAlterada,
}) => {
  const [dados, setDados] = useState<ElegibilidadeReserva | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<{ tipo: 'erro' | 'sem-permissao' | 'nao-encontrada'; mensagem: string } | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)

  const [editando, setEditando] = useState(false)
  const [faturamento, setFaturamento] = useState(0)
  const [imovel, setImovel] = useState<ValoresImovel>({
    valorImovel: 0,
    valorImovelFonte: 'ESTIMATIVA_CONSULTOR',
    valorSolicitado: 0,
  })
  const [salvando, setSalvando] = useState(false)
  const [erroSalvar, setErroSalvar] = useState<string | null>(null)

  const [reavaliando, setReavaliando] = useState(false)
  const [dialogo, setDialogo] = useState<'rota' | 'sobrepor' | null>(null)
  const [rotaNova, setRotaNova] = useState<RotaOperacao | ''>('')
  const [erroDialogo, setErroDialogo] = useState<string | null>(null)

  const admin = papel === 'ADMIN'
  const backoffice = ehBackoffice(papel)

  const aplicar = useCallback(
    (e: ElegibilidadeReserva) => {
      setDados(e)
      onAlterada?.(e)
    },
    [onAlterada]
  )

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setDados(await elegibilidadeReservaService.obter(oportunidadeId))
    } catch (e) {
      const status = (e as { status?: number } | null)?.status
      if (status === 403) setErro({ tipo: 'sem-permissao', mensagem: 'Sem permissão para ver a elegibilidade desta reserva.' })
      else if (status === 404) setErro({ tipo: 'nao-encontrada', mensagem: 'Elegibilidade não encontrada para esta reserva.' })
      else setErro({ tipo: 'erro', mensagem: 'Não foi possível carregar a elegibilidade da reserva.' })
    } finally {
      setCarregando(false)
    }
  }, [oportunidadeId])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const ultima = dados?.ultima ?? null
  // Dados declarados VIGENTES (Q69) vêm do servidor em `dadosDeclarados`; nulos chegam omitidos.
  const decl = dados?.dadosDeclarados ?? null
  const situacao = dados?.situacaoEncaminhamento ?? null
  const tipoPolitica = situacao?.politicaTipo ?? ultima?.politicaTipo ?? null
  const ltv = tipoPolitica === 'LTV_MAX' || decl?.valorImovel != null
  const rotaReserva = dados?.rota ?? null
  const rota = rotaReserva?.rotaOperacao ?? null
  const temRota = tipoPolitica === 'RECEIVABLES_RANGE' || rota != null
  const podeEditar = dados?.dadosEditaveis === true

  const abrirEdicao = () => {
    setFaturamento(decl?.faturamentoAnual ?? 0)
    setImovel({
      valorImovel: decl?.valorImovel ?? 0,
      valorImovelFonte: decl?.valorImovelFonte ?? 'ESTIMATIVA_CONSULTOR',
      valorSolicitado: decl?.valorSolicitado ?? 0,
    })
    setErroSalvar(null)
    setEditando(true)
  }

  const salvar = async () => {
    setSalvando(true)
    setErroSalvar(null)
    // PUT com semântica de SUBSTITUIÇÃO: manda sempre o conjunto inteiro. Fora do Home Equity os
    // campos do imóvel não aparecem, mas seguem com o valor declarado (senão o servidor os apagaria).
    // Valor solicitado derivado (= valor da operação) e não alterado continua derivado (nulo).
    const solicitadoInalteradoDerivado =
      decl?.valorSolicitadoDerivado === true && imovel.valorSolicitado === (decl.valorSolicitado ?? 0)
    try {
      const r = await elegibilidadeReservaService.atualizarDados(oportunidadeId, {
        faturamentoAnual: faturamento > 0 ? faturamento : null,
        valorImovel: imovel.valorImovel > 0 ? imovel.valorImovel : null,
        valorImovelFonte: imovel.valorImovel > 0 ? imovel.valorImovelFonte : null,
        valorSolicitado: imovel.valorSolicitado > 0 && !solicitadoInalteradoDerivado ? imovel.valorSolicitado : null,
      })
      aplicar(r)
      setEditando(false)
      setAviso('Dados de elegibilidade atualizados — o servidor gravou nova avaliação.')
    } catch (e) {
      setErroSalvar(mensagemErroElegibilidade(e, 'Não foi possível salvar os dados de elegibilidade.'))
    } finally {
      setSalvando(false)
    }
  }

  const reavaliar = async () => {
    setReavaliando(true)
    setAviso(null)
    try {
      aplicar(await elegibilidadeReservaService.reavaliar(oportunidadeId))
      setAviso('Reavaliação registrada com a política vigente.')
    } catch (e) {
      setAviso(mensagemErroElegibilidade(e, 'Não foi possível reavaliar a elegibilidade.'))
    } finally {
      setReavaliando(false)
    }
  }

  const confirmarDialogo = async (motivo: string) => {
    setErroDialogo(null)
    try {
      if (dialogo === 'rota') {
        if (!rotaNova) return
        aplicar(await elegibilidadeReservaService.alterarRota(oportunidadeId, { rota: rotaNova, motivo }))
        setAviso('Rota alterada.')
      } else {
        aplicar(
          await elegibilidadeReservaService.sobrepor(oportunidadeId, {
            motivo,
            politicaId: situacao?.politicaId ?? ultima?.politicaId ?? null,
            hashEntradas: situacao?.hashEntradas ?? ultima?.hashEntradas ?? null,
          })
        )
        setAviso('Impedimento sobreposto — vale enquanto os dados e a política não mudarem.')
      }
      setDialogo(null)
    } catch (e) {
      setErroDialogo(mensagemErroElegibilidade(e, 'Não foi possível registrar a decisão.'))
      // A avaliação mudou: recarrega para o ADMIN ver o novo resultado antes de decidir de novo.
      if ((e as { status?: number } | null)?.status === 409) void carregar()
    }
  }

  const corpo = () => {
    if (carregando) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} aria-label="Carregando elegibilidade" />
        </Box>
      )
    }
    if (erro) {
      return (
        <Alert
          severity={erro.tipo === 'erro' ? 'warning' : 'info'}
          action={
            erro.tipo === 'erro' ? (
              <Button color="inherit" size="small" onClick={() => void carregar()}>
                Tentar novamente
              </Button>
            ) : undefined
          }
        >
          {erro.mensagem}
        </Alert>
      )
    }
    return (
      <Stack spacing={2}>
        {aviso && <Alert severity="info" onClose={() => setAviso(null)}>{aviso}</Alert>}

        {ultima ? (
          <AlertaElegibilidade avaliacao={ultima} contexto={modo === 'cadastro' ? 'reserva' : 'generico'} />
        ) : (
          <Alert severity="info" data-testid="elegibilidade-sem-avaliacao">
            Ainda sem avaliação de elegibilidade registrada. Ela nasce quando os dados declarados são informados ou
            no encaminhamento ao parceiro.
          </Alert>
        )}

        {/* `impede` é o resultado do motor; bloqueado AGORA é `exigeSobreposicao` (impede e sem sobreposição). */}
        {modo !== 'cadastro' && situacao?.exigeSobreposicao && (
          <Alert severity="warning" data-testid="elegibilidade-impede-encaminhamento">
            Com a política vigente, esta reserva <strong>não pode ser encaminhada ao parceiro</strong>
            {situacao.resultado ? ` (${ROTULO_RESULTADO_ELEGIBILIDADE[situacao.resultado]})` : ''} até o ajuste
            dos dados ou a decisão do Administrador.
            {(situacao.explicacoes ?? []).map((e, i) => (
              <Typography key={i} variant="body2">
                • {e.mensagem}
              </Typography>
            ))}
          </Alert>
        )}
        {modo !== 'cadastro' && situacao?.impede && !situacao.exigeSobreposicao && situacao.sobreposicaoId && (
          <Alert severity="info" data-testid="elegibilidade-liberada-admin">
            Liberado por decisão do ADMIN
            {situacao.resultado ? ` (${ROTULO_RESULTADO_ELEGIBILIDADE[situacao.resultado]})` : ''}: o encaminhamento ao
            parceiro está liberado para estes dados e esta política. Se os dados, o valor ou a política mudarem, a
            decisão deixa de valer.
          </Alert>
        )}
        {modo === 'detalhe' && situacao?.divergeDaUltima && (
          <Alert severity="info">
            A política vigente ou os dados mudaram desde a última avaliação gravada.
            {backoffice ? ' Use "Reavaliar" para registrar o resultado atual.' : ''}
          </Alert>
        )}

        {/* Dados declarados (Q69) */}
        {!editando && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
            }}
          >
            <Linha
              rotulo="Faturamento anual declarado"
              valor={decl?.faturamentoAnual != null ? formatCurrency(decl.faturamentoAnual) : 'Não informado'}
              detalhe={
                decl?.faturamentoAnual != null
                  ? [
                      decl.faturamentoOrigem ? ROTULO_ORIGEM_FATURAMENTO[decl.faturamentoOrigem] : null,
                      decl.faturamentoDeclaradoEm ? `em ${formatDateTime(decl.faturamentoDeclaradoEm)}` : null,
                      decl.sequenciaAvaliacao > 0 ? `avaliação nº ${decl.sequenciaAvaliacao}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : null
              }
            />
            {decl?.valorOperacao != null && (
              <Linha rotulo="Valor da operação avaliado" valor={formatCurrency(decl.valorOperacao)} />
            )}
            {ltv && (
              <>
                <Linha
                  rotulo="Valor estimado do imóvel"
                  valor={decl?.valorImovel != null ? formatCurrency(decl.valorImovel) : 'Não informado'}
                  detalhe={decl?.valorImovelFonte ? ROTULO_FONTE_VALOR_IMOVEL[decl.valorImovelFonte] : null}
                />
                <Linha
                  rotulo="Valor solicitado"
                  valor={decl?.valorSolicitado != null ? formatCurrency(decl.valorSolicitado) : 'Não informado'}
                  detalhe={
                    [
                      decl?.valorSolicitadoDerivado ? 'igual ao valor da operação' : null,
                      (situacao?.ltvCalculado ?? ultima?.ltvCalculado) != null
                        ? `LTV ${fracaoComoPercentual((situacao?.ltvCalculado ?? ultima?.ltvCalculado) as number)} (servidor)`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ') || null
                  }
                />
              </>
            )}
          </Box>
        )}

        {modo === 'resumo' ? null : editando ? (
          <Stack spacing={2} component="section" aria-label="Editar dados de elegibilidade">
            <CurrencyField
              label="Faturamento anual declarado (R$)"
              value={faturamento}
              onChange={setFaturamento}
              fullWidth
              disabled={salvando}
              helperText={
                backoffice
                  ? 'Transcrição do back-office (fica registrada como tal, com data e autor).'
                  : 'Declarado pelo consultor (fica registrado com data e versão).'
              }
            />
            {ltv && <CamposImovel valores={imovel} onChange={setImovel} disabled={salvando} />}
            {erroSalvar && <Alert severity="error">{erroSalvar}</Alert>}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => setEditando(false)} disabled={salvando}>
                Cancelar
              </Button>
              <Button variant="contained" onClick={() => void salvar()} disabled={salvando}>
                {salvando ? 'Salvando…' : 'Salvar dados'}
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {podeEditar && (
              <Button size="small" variant="outlined" onClick={abrirEdicao}>
                {backoffice ? 'Transcrever dados declarados' : 'Editar dados declarados'}
              </Button>
            )}
            {modo === 'detalhe' && backoffice && (
              <Button size="small" variant="outlined" onClick={() => void reavaliar()} disabled={reavaliando}>
                {reavaliando ? 'Reavaliando…' : 'Reavaliar'}
              </Button>
            )}
            {modo === 'detalhe' && admin && temRota && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setRotaNova('')
                  setErroDialogo(null)
                  setDialogo('rota')
                }}
              >
                Alterar rota
              </Button>
            )}
            {modo === 'detalhe' && admin && situacao?.exigeSobreposicao && (
              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={() => {
                  setErroDialogo(null)
                  setDialogo('sobrepor')
                }}
              >
                Sobrepor impedimento
              </Button>
            )}
          </Stack>
        )}

        {modo === 'detalhe' && (dados?.historico?.length ?? 0) > 0 && (
          <>
            <Divider />
            <Box component="section" aria-label="Histórico de avaliações">
              <Typography variant="overline">Histórico de avaliações</Typography>
              <Stack spacing={0.75}>
                {dados!.historico!.map((h) => (
                  <Stack key={h.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <ChipResultadoElegibilidade resultado={h.resultado} />
                    <Typography variant="caption" color="text.secondary">
                      v{h.sequencia} · {ROTULO_MOMENTO_AVALIACAO[h.momento] ?? h.momento} · {formatDateTime(h.avaliadoEm)}
                      {h.politicaVersao != null ? ` · política v${h.politicaVersao}` : ''}
                      {h.politicaVigenciaInicio ? ` de ${formatarDataIso(h.politicaVigenciaInicio)}` : ''}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </>
        )}

        {modo === 'detalhe' && (dados?.decisoes?.length ?? 0) > 0 && (
          <Box component="section" aria-label="Decisões do Administrador">
            <Typography variant="overline">Decisões do Administrador</Typography>
            <Stack spacing={0.75}>
              {dados!.decisoes!.map((d) => (
                <Box key={d.id}>
                  <Typography variant="body2">
                    <strong>{ROTULO_DECISAO_ELEGIBILIDADE[d.tipo] ?? d.tipo}</strong>
                    {d.tipo === 'ALTERAR_ROTA' && d.rotaNova
                      ? ` → ${ROTULO_ROTA_OPERACAO[d.rotaNova]}`
                      : ''}
                    : {d.motivo}
                    {d.tipo === 'SOBREPOR_IMPEDIMENTO' && !d.vigente ? ' (não vale mais: dados ou política mudaram)' : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(d.ocorridoEm)}
                    {/* Consultor recebe sem autor (Q-PP1-7): só mostra quando veio. */}
                    {d.autorNome ? ` · ${d.autorNome}` : ''}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    )
  }

  return (
    <Card sx={{ mb: 3 }} component="section" aria-label="Elegibilidade da reserva">
      <CardHeader
        title="Elegibilidade"
        subheader={
          rota ? (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap component="span">
              <ChipRotaOperacao rota={rota} mostrarPadrao />
              <Typography variant="caption" color="text.secondary" component="span">
                {rotaReserva?.rotaOrigem ? ROTULO_ORIGEM_ROTA[rotaReserva.rotaOrigem] : ''}
                {rotaReserva?.rotaDefinidaEm ? ` · ${formatDateTime(rotaReserva.rotaDefinidaEm)}` : ''}
                {rotaReserva?.divergeDaCalculada && rotaReserva.rotaCalculada
                  ? ` · a política sugere ${ROTULO_ROTA_OPERACAO[rotaReserva.rotaCalculada]}`
                  : ''}
              </Typography>
            </Stack>
          ) : undefined
        }
      />
      <CardContent>{corpo()}</CardContent>

      <DialogoMotivoElegibilidade
        aberto={dialogo === 'rota'}
        titulo="Alterar rota da operação"
        rotuloConfirmar="Alterar rota"
        podeConfirmar={!!rotaNova}
        erro={erroDialogo}
        onConfirmar={confirmarDialogo}
        onFechar={() => setDialogo(null)}
      >
        <Typography variant="body2">
          Rota atual: <strong>{rota ? ROTULO_ROTA_OPERACAO[rota] : 'não definida'}</strong>
          {(rotaReserva?.rotaCalculada ?? ultima?.rotaCalculada)
            ? ` · sugerida pela política: ${ROTULO_ROTA_OPERACAO[(rotaReserva?.rotaCalculada ?? ultima?.rotaCalculada)!]}`
            : ''}
        </Typography>
        <RadioGroup
          aria-label="Nova rota"
          value={rotaNova}
          onChange={(e) => setRotaNova(e.target.value as RotaOperacao)}
        >
          {(Object.keys(ROTULO_ROTA_OPERACAO) as RotaOperacao[]).map((r) => (
            <FormControlLabel key={r} value={r} control={<Radio size="small" />} label={ROTULO_ROTA_OPERACAO[r]} />
          ))}
        </RadioGroup>
      </DialogoMotivoElegibilidade>

      <DialogoMotivoElegibilidade
        aberto={dialogo === 'sobrepor'}
        titulo="Sobrepor impedimento"
        rotuloConfirmar="Sobrepor impedimento"
        erro={erroDialogo}
        onConfirmar={confirmarDialogo}
        onFechar={() => setDialogo(null)}
      >
        <Typography variant="body2">
          A sobreposição libera o encaminhamento ao parceiro <strong>para estes dados e esta política</strong>. Se
          os dados declarados, o valor ou a política mudarem, ela deixa de valer.
        </Typography>
        {(situacao?.explicacoes ?? ultima?.explicacoes ?? []).map((e, i) => (
          <Typography key={i} variant="body2">
            • {e.mensagem}
          </Typography>
        ))}
      </DialogoMotivoElegibilidade>
    </Card>
  )
}

export default SecaoElegibilidadeReserva
